import { Stack, Typography } from '@mui/material';
import type { Account, CloudAccountConfiguration } from '@repo/api-gw-sdk';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { ReadMoreLink } from '@/components/shared/readMoreLink';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';

import {
  RestoreAccountConfigFlow,
  type RestoreAccountConfigState,
} from './restoreAccountConfigFlow';

interface RestoreAccountConfigPanelProps {
  account: Account;
  config: CloudAccountConfiguration;
  onUpdate: () => Promise<void>;
  onClose: () => void;
}

export function RestoreServerConnectivityPanel(
  props: RestoreAccountConfigPanelProps
) {
  const dal = useDAL();

  const onSave = async (state: RestoreAccountConfigState) => {
    await dal.cloudAccounts.restore.update(state.account.id, {
      ...state.account,
      overrideDefaultNetworkConfig: !!state.config.awsConfig.length,
    });

    await dal.cloudAccounts.restore.configuration.update(
      state.account.id,
      state.config
    );
    await props.onUpdate();
    return true;
  };

  return (
    <PanelWrapper
      testIdPrefix='restore-account-config'
      header={{
        title: 'Edit restore server connectivity',
        subtitle: (
          <Stack className='px-[20px]' gap='24px'>
            <CloudProviderIndicator
              cloudProvider={props.account.cloudProvider}
              text={props.account.providerAccountId}
            />
            <Stack gap='12px' className='mb-[24px]'>
              <Typography className='max-w-[680px]' variant='body1'>
                {`Restore operations run on a temporary server. Before creating it, Eon selects an availability zone and security group based on the restore destination and resource type. If your default VPC resources have outbound limitations or were deleted, you’ll need to customize the connectivity. `}
                <ReadMoreLink href='/docs/user-guide/configuration/connect-your-cloud-accounts/restore-accounts/restore-server-connectivity-requirements' />
              </Typography>
            </Stack>
          </Stack>
        ),
        onBack: props.onClose,
        onClose: props.onClose,
      }}
    >
      <Wizard<RestoreAccountConfigState>
        hideBackArrow={true}
        onAbort={props.onClose}
        onFinish={onSave}
        flow={[RestoreAccountConfigFlow]}
        initialState={{
          account: props.account,
          config: { ...props.config },
        }}
      />
    </PanelWrapper>
  );
}
